import { apiClient } from '@/lib/api/client';
import { endpoints } from '@/lib/api/endpoints';

import {
  warrantyClaimDetailSchema,
  warrantyClaimSchema,
  warrantyEligibilitySchema,
} from './schemas';
import type {
  CreateClaimInput,
  RespondToClaimInput,
  WarrantyClaim,
  WarrantyClaimDetail,
  WarrantyEligibility,
} from './types';

/** Whether the device can raise a claim right now, and (if expired) when it lapsed. */
export async function getWarrantyEligibility(deviceId: string): Promise<WarrantyEligibility> {
  return warrantyEligibilitySchema.parse(
    await apiClient.get(endpoints.warranty.eligibility(deviceId)),
  );
}

/**
 * The grower's full warranty-claim list (story 101872). Filtering + search run
 * client-side off this list (the seed is small), so the page stays responsive while
 * the filter drawer is open.
 */
export async function listWarrantyClaims(): Promise<WarrantyClaim[]> {
  const res = await apiClient.getList(endpoints.warranty.claims);
  return res.data.map((c) => warrantyClaimSchema.parse(c));
}

/** Full claim detail: timeline, evidence, Admin comments, shipment (story 101872). */
export async function getWarrantyClaim(id: string): Promise<WarrantyClaimDetail> {
  return warrantyClaimDetailSchema.parse(await apiClient.get(endpoints.warranty.detail(id)));
}

/**
 * Submit a warranty claim. The backend re-runs the eligibility checks (within warranty,
 * ≥1 photo, no open claim) before persisting with status Submitted; a failed check surfaces
 * as an `ApiError` (code WARRANTY_ERROR_CODES.notEligible).
 */
export async function createWarrantyClaim(input: CreateClaimInput): Promise<WarrantyClaim> {
  return warrantyClaimSchema.parse(await apiClient.post(endpoints.warranty.claims, input));
}

/**
 * Reply to an Info Request with a comment (+ optional photos). The claim returns to Under
 * Review for the Admin to continue the review (story 101874).
 */
export async function respondToClaim(
  id: string,
  input: RespondToClaimInput,
): Promise<WarrantyClaimDetail> {
  return warrantyClaimDetailSchema.parse(
    await apiClient.post(endpoints.warranty.respond(id), input),
  );
}

/** Mark a claim's latest update as viewed — clears the row's unread/response indicator. */
export async function markClaimViewed(id: string): Promise<void> {
  await apiClient.post(endpoints.warranty.markViewed(id));
}
